import { action, makeObservable, observable } from "mobx";
import { dictionary } from "../../languages";
import { AppConfig, AppLang } from "./types";
import ConfigService from "../../services/ConfigService";
import caseStore from "../CaseStore";
import offerStore from "../OfferStore";
import notificationStore from "../NotificationStore";
import lovStore from "../LovStore";
import { Lov } from "../LovStore/LovStore";
import { Flags } from "./flags";

const appConfigDefaults: AppConfig = {
  dictionary: null,
  featureFlags: [],
  offerRanges: ["low", "mid", "high", "unset"],
  caseLimit: 10,
  offerLimit: 20,
  userCases: [],
  caseSearch: [],
  delegationCases: [],
  userOffers: [],
  lawyerOffers: [],
  userNotifications: [],
  notificationInterval: 60000,
  sessionCount: 0,
  caseCategoriesUpdatedAt: 0,
  caseStatistics: {
    mostCaseCategories: [],
    mostHandShakeCases: [],
    mostOfferedCases: [],
  },
};

class ConfigStore {
  @observable currentLanguage: AppLang = "tr";
  @observable appConfig: AppConfig = appConfigDefaults;
  configService: ConfigService;
  localDictionaryCache: Lov | null;

  constructor() {
    makeObservable(this);
    this.configService = new ConfigService();

    // Get current language
    this.currentLanguage =
      (localStorage.getItem("preferredLanguage") as AppLang) ||
      navigator.language?.split("-")[0] ||
      "tr";
    dictionary.setLanguage(this.currentLanguage);

    // Get local dictionary cache
    this.localDictionaryCache = JSON.parse(
      localStorage.getItem(`dictionary-${this.currentLanguage}`) || "{}"
    );

    if (this.localDictionaryCache?.value) {
      dictionary.setContent({
        [this.currentLanguage]: this.localDictionaryCache?.value,
      });
    }
  }

  @action
  initAppConfig = (): Promise<boolean> => {
    return this.configService.initApp(this.currentLanguage).then((result) => {
      this.appConfig = {
        ...appConfigDefaults,
        ...result,
      };

      if (this.appConfig.userCases) {
        caseStore.userCases = this.appConfig.userCases;
      }

      if (this.appConfig.userOffers) {
        offerStore.userOffers = this.appConfig.userOffers;
      }

      if (this.appConfig.userNotifications) {
        notificationStore.notifications = this.appConfig.userNotifications;
      }

      if (this.appConfig.userOffers) {
        offerStore.userOffers = this.appConfig.userOffers;
      }

      if (this.appConfig.lawyerOffers) {
        offerStore.lawyerOffers = this.appConfig.lawyerOffers;
      }

      if (this.appConfig.caseSearch) {
        caseStore.caseSearchResult = this.appConfig.caseSearch;
      }

      if (this.appConfig.delegationCases) {
        caseStore.delegationSearchResult = this.appConfig.delegationCases;
      }

      if (result.userNotifications) {
        notificationStore.notifications = result.userNotifications;
      }

      return true;
    });
  };

  fetchDictionaryContent = async () => {
    if (this.appConfig.dictionary) {
      this.setDictionaryContent(this.appConfig.dictionary.value);
      localStorage.setItem(
        `dictionary-${this.currentLanguage}`,
        JSON.stringify(this.appConfig.dictionary)
      );
    } else if (this.localDictionaryCache?.value) {
      this.setDictionaryContent(this.localDictionaryCache.value);
    } else {
      await lovStore
        .fetchLovByCode(
          `lang-${this.currentLanguage}-all`,
          `lang-${this.currentLanguage}`
        )
        .then((data) => {
          if (data?.value && Object.keys(data?.value || {}).length > 0) {
            this.setDictionaryContent(data.value);
            localStorage.setItem(
              `dictionary-${this.currentLanguage}`,
              JSON.stringify(data)
            );
          }
        });
    }
  };

  @action
  setCurrentLanguage = (language: AppLang): void => {
    localStorage.setItem("preferredLanguage", language);
    window.location.reload();
  };

  @action
  setDictionaryContent = (content: any): void => {
    dictionary.setContent({ [this.currentLanguage]: content });
  };

  @action
  fetchFeatureFlags = async (): Promise<void> => {
    const flags = await this.configService.fetchFeatureFlags();
    this.appConfig.featureFlags = flags;
  };

  getFlag = (flagName: Flags) => {
    const flag = this.appConfig.featureFlags?.find(
      (item) => item.name === flagName.toString()
    );

    if (flag?.type === "boolean") {
      return flag.value as Boolean;
    } else if (flag?.type === "string") {
      return flag.value as string;
    } else if (flag?.type === "number") {
      return flag.value as number;
    } else if (flag?.type === "json") {
      return flag.value as any;
    }
  };
}

export default ConfigStore;
